import type * as zod from 'zod';

import {
  type CartesianPose,
  EulerAngleToQuaternion,
  ZERO_POSE,
  additionBetweenPoses,
  invertPose,
  tooltipCoordinatesToBaseCoordinates,
} from '@sb/geometry';
import { makeNamespacedLog } from '@sb/log';
import {
  TeleopMode,
  TeleopStatus,
} from '@sb/messenger/auto_generated/messenger/teleop_bot_service';
import {
  type ArmTarget,
  DEFAULT_TCP_OFFSET_OPTION,
  type TCPOffsetOption,
} from '@sb/motion-planning';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { DEFAULT_SPEED_PROFILE } from '@sb/routine-runner/speed-profile/SpeedProfile';
import {
  createEventDrivenWaiter,
  type EventDrivenWaiter,
} from '@sb/routine-runner/util/eventDrivenWaiter';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import { computeMoveToStart } from './computeMoveToStart';
import {
  closestApproachMeters,
  computeSphereWindow,
} from './computeSphereWindow';
import type { DemonstrationSkillRow } from './resolveSkillRecording';
import { resolveSkillRecording } from './resolveSkillRecording';
import { resolveSphereScan } from './resolveSphereScan';
import { rotvecFrameToPose } from './rotvecFrameToPose';
import Variables from './Variables';

const log = makeNamespacedLog('RunDemonstrationStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

// Replay completion is detected via a pushed StateChanged event from
// teleop-bot. This interval is only the reconcile backstop that re-checks state
// if an event is ever dropped — it is not a hot poll.
const RECONCILE_INTERVAL_MS = 1000;
// Stuck-session fallback only — normal exit is the STOPPED branch of the
// replay loop. Long manual captures can exceed 10 min; 3600s is headroom.
const MAX_REPLAY_SECONDS = 3600;
const DEMONSTRATION_CLIENT_ID = 'demonstration-replay';

// Build a CartesianPose from the recorder's gripper TCP offset (wrist→TCP):
// position [x,y,z] (m) + rotation [rx,ry,rz] (RADIANS, Three.js XYZ order).
function gripperTcpToPose(
  position: number[],
  rotation: number[],
): CartesianPose {
  const q = EulerAngleToQuaternion({
    x: rotation[0] ?? 0,
    y: rotation[1] ?? 0,
    z: rotation[2] ?? 0,
  });

  return {
    x: position[0] ?? 0,
    y: position[1] ?? 0,
    z: position[2] ?? 0,
    ...q,
  };
}

async function fetchRecordingFrames(
  feathersApp:
    | { service(name: string): { get(id: string): Promise<unknown> } }
    | undefined,
  recordingId: string,
): Promise<{
  // Recorded wrist frames (pose + recorder timestamp in seconds), in order.
  frames: Array<{ pose: CartesianPose; timestamp: number }>;
  first: CartesianPose;
  last: CartesianPose;
  // Reference's world pose at record time. Undefined ⇒ world-frame recording.
  refWorldPose: CartesianPose | undefined;
  // tcpOption the reference was saved in (`'wrist'`, `'ee-*'`, …).
  referenceRefTcpOption: string | undefined;
  // Record-time gripper TCP offset (wrist→TCP), from carried metadata.json.
  recordTcpOffset: CartesianPose | undefined;
  // Folded into the cache key so a re-uploaded recording isn't served stale.
  updatedAt: string | undefined;
  // True when `frames` was narrowed to persisted idle-trim bounds; forwarded
  // to teleop so the window timestamps fire even without sphere-trim.
  hasIdleTrim: boolean;
}> {
  if (!feathersApp) {
    throw new Error(
      'Demonstration replay requires a feathers client on routine context (feathersApp)',
    );
  }

  const body = (await feathersApp
    .service('demonstration-recordings')
    .get(recordingId)) as {
    updatedAt?: string;
    referenceFrame?: { refWorldPose?: CartesianPose; refTcpOption?: string };
    firstMotionTimestamp?: number | null;
    lastMotionTimestamp?: number | null;
    data?: {
      files?: {
        pose?: {
          frames?: Array<{
            timestamp?: number;
            translation: { x: number; y: number; z: number };
            rotation: { x: number; y: number; z: number };
          }>;
        };
      };
    };
    metadata?: {
      gripper_tcp_position?: number[] | null;
      gripper_tcp_rotation?: number[] | null;
    } | null;
  };

  const frames = body?.data?.files?.pose?.frames;

  if (!frames || frames.length === 0) {
    throw new Error(
      `Demonstration recording ${recordingId} has no pose frames`,
    );
  }

  const meta = body?.metadata;

  // Recorder stores the offset in tooltip coords; lift to base to compose
  // with the base-aligned live offset and recorded frames.
  const recordTcpOffset =
    meta?.gripper_tcp_position && meta?.gripper_tcp_rotation
      ? tooltipCoordinatesToBaseCoordinates(
          gripperTcpToPose(
            meta.gripper_tcp_position,
            meta.gripper_tcp_rotation,
          ),
        )
      : undefined;

  const allPosed = frames.map((f) => {
    // Timestamps are required seconds — a frame-index fallback would silently
    // disagree on units with the forwarded replayStart/EndTimestamp wire.
    if (typeof f.timestamp !== 'number') {
      throw new Error(
        'demonstration recording frame missing required timestamp',
      );
    }

    return { pose: rotvecFrameToPose(f), timestamp: f.timestamp };
  });

  // Auto-trim idle lead-in/out using persisted bounds; missing bounds (older
  // recordings) or an entirely-idle recording pass the full array through.
  const firstMotionTs = body?.firstMotionTimestamp ?? null;
  const lastMotionTs = body?.lastMotionTimestamp ?? null;

  const hasIdleTrim = firstMotionTs !== null && lastMotionTs !== null;

  const posed = hasIdleTrim
    ? allPosed.filter(
        (f) => f.timestamp >= firstMotionTs && f.timestamp <= lastMotionTs,
      )
    : allPosed;

  if (posed.length === 0) {
    throw new Error(
      `Demonstration recording ${recordingId} has no frames after idle-trim`,
    );
  }

  return {
    frames: posed,
    first: posed[0]!.pose,
    last: posed[posed.length - 1]!.pose,
    refWorldPose: body?.referenceFrame?.refWorldPose,
    referenceRefTcpOption: body?.referenceFrame?.refTcpOption,
    recordTcpOffset,
    updatedAt: body?.updatedAt,
    hasIdleTrim,
  };
}

// Fetched recording + resolved sphere-trim window indices (inclusive).
// Trim disabled ⇒ startIndex=0, endIndex=frames.length-1.
interface ResolvedRecording {
  frames: Array<{ pose: CartesianPose; timestamp: number }>;
  first: CartesianPose;
  last: CartesianPose;
  refWorldPose: CartesianPose | undefined;
  referenceRefTcpOption: string | undefined;
  recordTcpOffset: CartesianPose | undefined;
  startIndex: number;
  endIndex: number;
  hasIdleTrim: boolean;
}

// Process-lifetime cache keyed by `${recordingId}:${radius}:${updatedAt}` so
// a re-uploaded / patched recording invalidates its entries automatically.
// Bounded so a long-lived runner replaying many recordings can't grow it
// without limit (oldest entry evicted on overflow).
const MAX_RESOLVED_RECORDING_CACHE = 50;
const resolvedRecordingCache = new Map<string, ResolvedRecording>();

// Fetch a recording and resolve its sphere-trim window (the last maximal
// contiguous in-sphere run). radiusMeters=null disables the trim (no cache).
async function loadRecordingWindow(
  feathersApp:
    | { service(name: string): { get(id: string): Promise<unknown> } }
    | undefined,
  recordingId: string,
  radiusMeters: number | null,
  pin: 'start' | 'end' | 'reference',
  pickedTcpOption: string | undefined,
  anchorOnReference: boolean,
  liveTcpOffset: CartesianPose | undefined,
  // Skill's activeTooltipOption — preferred over the recording's stored refTcpOption as the trim anchor frame.
  skillTooltipOption?: string,
): Promise<ResolvedRecording> {
  const {
    frames,
    first,
    last,
    refWorldPose,
    referenceRefTcpOption,
    recordTcpOffset,
    updatedAt,
    hasIdleTrim,
  } = await fetchRecordingFrames(feathersApp, recordingId);

  const effectiveRefTcpOption = skillTooltipOption ?? referenceRefTcpOption;

  const effectivePickedTcpOption = anchorOnReference
    ? effectiveRefTcpOption
    : pickedTcpOption;

  const cacheKey =
    radiusMeters !== null && recordTcpOffset
      ? `${recordingId}:${radiusMeters}:${pin}:${effectivePickedTcpOption ?? ''}:${effectiveRefTcpOption ?? ''}:${updatedAt ?? ''}`
      : null;

  if (cacheKey) {
    const hit = resolvedRecordingCache.get(cacheKey);
    if (hit) return hit;
  }

  let startIndex = 0;
  let endIndex = frames.length - 1;

  if (radiusMeters !== null) {
    const scan = resolveSphereScan({
      pin,
      frames: frames.map((f) => f.pose),
      refWorldPose,
      refTcpOption: effectiveRefTcpOption,
      pickedTcpOption: effectivePickedTcpOption,
      recordedFrame: 'wrist',
      recordTcp: recordTcpOffset,
      liveTcp: liveTcpOffset,
    });

    if (!scan) {
      throw new Error(
        `RunDemonstration sphere trim could not resolve the scan frame ` +
          `(pin=${pin}, picked=${effectivePickedTcpOption ?? 'wrist'}). Likely missing ` +
          `gripper TCP info on a recording that needs it.`,
      );
    }

    const window = computeSphereWindow(
      scan.framePositions,
      scan.sphereCenter,
      radiusMeters,
      scan.side,
    );

    if (!window) {
      const closest = closestApproachMeters(
        scan.framePositions,
        scan.sphereCenter,
      );

      // Name the exact tooltip the distance was measured at.
      const anchorOption =
        (pin === 'reference'
          ? effectiveRefTcpOption ?? effectivePickedTcpOption
          : effectivePickedTcpOption) ?? 'wrist';

      const anchorTcp = anchorOption.startsWith('ee-');

      const anchorLabel = refWorldPose
        ? `${anchorTcp ? 'gripper tip' : 'wrist'} (tooltip '${anchorOption}') comes no closer to the reference frame than`
        : `wrist (tooltip '${anchorOption}') comes no closer to the final recorded pose than`;

      throw new Error(
        `RunDemonstration sphere radius ${radiusMeters.toFixed(4)} m selects no ` +
          `recorded frames — the ${anchorLabel} ` +
          `${closest.toFixed(4)} m. Increase the radius to at least that.`,
      );
    }

    startIndex = window.k;
    endIndex = window.m;
  }

  const resolved: ResolvedRecording = {
    frames,
    first,
    last,
    refWorldPose,
    referenceRefTcpOption,
    recordTcpOffset,
    startIndex,
    endIndex,
    hasIdleTrim,
  };

  if (cacheKey) {
    if (
      !resolvedRecordingCache.has(cacheKey) &&
      resolvedRecordingCache.size >= MAX_RESOLVED_RECORDING_CACHE
    ) {
      const oldest = resolvedRecordingCache.keys().next().value;
      if (oldest !== undefined) resolvedRecordingCache.delete(oldest);
    }

    resolvedRecordingCache.set(cacheKey, resolved);
  }

  return resolved;
}

// Mirrors `getExpectedControlSource` in libs/sb-robot-api's teleop controller
// — duplicated so routine-runner doesn't import sb-robot-api's HTTP layer.
function getExpectedControlSource(): string {
  if (getFeatureFlagSync('complianceMode', false)) {
    return 'COMPLIANCE_JOINT_TRAJECTORY';
  }

  if (getFeatureFlagSync('zmqPoseMigration', false)) {
    return 'TELEOP';
  }

  return 'ROS_POSE';
}

export default class RunDemonstrationStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  // Set by _stop/_pause so the _play poll loop exits promptly.
  private stopRequested = false;

  // Guards issueStopTeleop so this step instance tears down teleop at most once
  // per play. _stop and the pollReplayUntilDone finally can both reach it, and a
  // stopped step's orphaned _play loop may run its finally late — after a new
  // replay session has already started. Without this, that late teardown would
  // StopTeleop the *next* session.
  private teleopStopped = false;

  // The replay loop's event-driven waiter, so _stop/_pause can wake its
  // in-flight wait() immediately instead of waiting up to a reconcile interval.
  // Set while pollReplayUntilDone runs; null otherwise.
  private replayWaiter: EventDrivenWaiter | null = null;

  // Set by _pause regardless of args.reason — reasonless pauses must still
  // take the fail() branch.
  private isPaused = false;

  private pauseReason: string | undefined;

  // In-flight move-to-start motion. Held so _stop/_pause can cancel.
  private activeMotion: ReturnType<
    RunDemonstrationStep['routineContext']['doMotion']
  > | null = null;

  // Frame re-expression (record TCP · inv(live TCP)) sent to teleop.
  private demonstrationFrameTransform: CartesianPose | undefined;

  // Sphere-trim window timestamps (recorder seconds) sent to teleop.
  private replayStartTimestamp: number | undefined;

  private replayEndTimestamp: number | undefined;

  // Recording id resolved from the demonstration Skill at the top of _play.
  private resolvedRecordingId: string | undefined;

  // Tooltip the skill was recorded with (`shared.activeTooltipOption`) —
  // preferred over the recording's stored refTcpOption for sphere-trim.
  private skillActiveTooltipOption: string | undefined;

  // Fetch the demonstration Skill row and pull its newest recording id
  // (`shared.recordings.at(-1)`) — a re-record appends, so the last entry is
  // the most recent demonstration. Throws a user-visible message on any
  // mismatch.
  private async resolveRecordingId(): Promise<string> {
    if (!('skillId' in this.args)) {
      return this.args.recordingId;
    }

    const { skillId } = this.args;

    const { feathersApp } = this.routineContext;

    if (!feathersApp) {
      throw new Error(
        'RunDemonstration requires a feathers client on routine context',
      );
    }

    // Wrap feathers NotFound so we surface a clean "skill X not found".
    let skill: DemonstrationSkillRow | undefined;

    try {
      skill = (await feathersApp.service('skills').get(skillId)) as
        | DemonstrationSkillRow
        | undefined;
    } catch (e) {
      throw new Error(
        `RunDemonstration: skill ${skillId} not found: ${(e as Error)?.message ?? e}`,
      );
    }

    // Validation + newest-recording selection is a pure function so it can be
    // unit-tested apart from the feathers fetch above.
    const { recordingId, activeTooltipOption } = resolveSkillRecording(
      skillId,
      skill,
    );

    this.skillActiveTooltipOption = activeTooltipOption;

    return recordingId;
  }

  // Returns fail() when paused so _play doesn't resolve cleanly on pause.
  private failIfPaused(
    fail: StepPlayArguments['fail'],
  ): Promise<void> | undefined {
    if (!this.isPaused) return undefined;

    return fail({
      failure: {
        kind: FailureKind.StepPlayFailure,
        stepKind: 'RunDemonstration',
      },
      failureReason: this.pauseReason || 'Demonstration replay was paused',
    });
  }

  // Mirrors RunSkillStep/TeleopStep — long-lived; no fast self-timeout.
  public hasSelfTimeout(): boolean {
    return false;
  }

  public initializeVariableState(): void {
    // completedCount carries across plays so a parent loop cycles through
    // position-list entries (mirrors MoveArmTo / Waypoint).
    const { completedCount = 0 } = this.variablesForInitialization;

    this.variables = {
      recordingId: null,
      completedCount,
      liveReferencePose: null,
    };
  }

  public async _play(playArgs: StepPlayArguments): Promise<void> {
    const { fail } = playArgs;
    const { teleopBotConnector } = this.routineContext;

    if (!teleopBotConnector) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunDemonstration',
        },
        failureReason: 'Unable to run demonstration due to service error',
      });
    }

    this.resetPlayState();

    let recordingId: string;

    try {
      recordingId = await this.resolveRecordingId();
    } catch (e) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunDemonstration',
        },
        failureReason:
          (e as Error)?.message ?? 'Failed to resolve demonstration skill',
      });
    }

    this.resolvedRecordingId = recordingId;
    this.setVariable('recordingId', recordingId);

    let startPose: CartesianPose;

    try {
      startPose = await this.resolveStartPose();
    } catch (e) {
      log.error(
        'demonstration.resolveStartPose',
        'Failed to resolve move-to-start pose',
        { e },
      );

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunDemonstration',
        },
        failureReason:
          (e as Error)?.message ?? 'Failed to resolve demonstration start pose',
      });
    }

    // A `_stop` during resolveStartPose has no activeMotion to cancel — bail
    // before kicking off the move.
    if (this.stopRequested) {
      return await this.failIfPaused(fail);
    }

    const sessionOutcome = await this.startReplaySession(
      startPose,
      recordingId,
      fail,
    );

    if (sessionOutcome === 'failed') {
      return undefined;
    }

    return this.pollReplayUntilDone(fail);
  }

  // Reset per-play state. Runs BEFORE any await in _play so a stop arriving
  // during the async phases isn't silently overwritten.
  private resetPlayState(): void {
    this.stopRequested = false;
    this.teleopStopped = false;
    this.isPaused = false;
    this.pauseReason = undefined;

    this.resolvedRecordingId = undefined;

    // Clear stale step variables so the UI doesn't keep showing a previous
    // play's recording id while this play resolves.
    this.setVariable('recordingId', null);
    this.setVariable('liveReferencePose', null);
  }

  // Stage the teleop session config (in parallel with the move-to-start),
  // move to startPose, clear the guided-mode check, acquire the control
  // source, and dispatch StartTeleop. 'failed' ⇒ fail()/teardown handled.
  private async startReplaySession(
    startPose: CartesianPose,
    recordingId: string,
    fail: StepPlayArguments['fail'],
  ): Promise<'failed' | 'ok'> {
    const teleopBotConnector = this.routineContext.teleopBotConnector!;

    // Captured as a tri-state so no unhandled rejection fires if moveArmTo
    // throws before we await; rethrown in the try below so failures route
    // through the same issueStopTeleop teardown.
    const sessionConfigPromise = teleopBotConnector
      .SetTeleopSessionConfig({
        // Must be an array even when empty or the encoder throws
        // `TypeError: e.bots is not iterable`.
        bots: [],
        modeOverride: TeleopMode.TELEOP_MODE_DEMONSTRATION_REPLAY,
        demonstrationSession: {
          recordingId,
          frameTransform: this.demonstrationFrameTransform,
          replayStartTimestamp: this.replayStartTimestamp,
          replayEndTimestamp: this.replayEndTimestamp,
          playbackSpeedMultiplier: this.args.speed,
        },
      })
      .then(
        () => ({ ok: true as const }),
        (error: unknown) => ({ ok: false as const, error }),
      );

    const moveResult = await this.moveArmTo(startPose, fail);

    if (moveResult === 'failed') {
      // fail() was already invoked with the real reason — don't fail() again.
      await this.issueStopTeleop();

      return 'failed';
    }

    if (moveResult === 'cancelled') {
      // Cancelled, not failed — fail() only if the cancel came from a pause.
      await this.issueStopTeleop();
      await this.failIfPaused(fail);

      return 'failed';
    }

    // Stop between motion completion and the teleop dispatch.
    if (this.stopRequested) {
      await this.issueStopTeleop();
      await this.failIfPaused(fail);

      return 'failed';
    }

    // Disable the guided-mode heartbeat for the replay phase — replay drives
    // via teleop-bot, so nothing would call validateGuidedMode and the timer
    // would pause us. Do NOT clear shouldNextArmMoveBeGuidedMode: the
    // move-to-start above is a real motion and must still require engagement.
    const stateBeforeReplay = this.routineContext.getRoutineRunnerState();

    if (stateBeforeReplay.kind === 'RoutineRunning') {
      this.routineContext.setRoutineRunnerState({
        ...stateBeforeReplay,
        shouldCheckGuidedMode: false,
      });
    }

    log.info('demonstration.start', 'Starting demonstration replay session', {
      recordingId,
    });

    try {
      // Await the staged config before StartTeleop so the activated session
      // uses it.
      const sessionConfigResult = await sessionConfigPromise;
      if (!sessionConfigResult.ok) throw sessionConfigResult.error;
    } catch (e) {
      log.error(
        'demonstration.start',
        'SetTeleopSessionConfig dispatch failed',
        { e },
      );

      await this.issueStopTeleop();

      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunDemonstration',
        },
        failureReason: 'Unable to run demonstration due to service error',
      });

      return 'failed';
    }

    // The HTTP /api/v1/teleop/start endpoint acquires the CSM control source
    // before StartTeleop; we dispatch StartTeleop directly via messenger, so
    // acquire it ourselves or teleop-bot's poses would be ignored.
    if (getFeatureFlagSync('useControlSourceManager', false)) {
      const source = getExpectedControlSource();

      try {
        await this.routineContext.armControlBotConnector?.SetControlSource({
          value: source,
          clientId: DEMONSTRATION_CLIENT_ID,
        });
      } catch (e) {
        log.error('demonstration.start', 'SetControlSource dispatch failed', {
          e,
        });

        // Clear staged session overrides — symmetric with the StartTeleop
        // catch below.
        await this.issueStopTeleop();

        await fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'RunDemonstration',
          },
          failureReason: 'Unable to run demonstration due to service error',
        });

        return 'failed';
      }
    }

    try {
      await teleopBotConnector.StartTeleop({
        clientId: DEMONSTRATION_CLIENT_ID,
      });
    } catch (e) {
      log.error('demonstration.start', 'StartTeleop dispatch failed', { e });

      // Clear staged session overrides so a failed start doesn't leak into
      // the next step.
      await this.issueStopTeleop();

      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunDemonstration',
        },
        failureReason: 'Unable to run demonstration due to service error',
      });

      return 'failed';
    }

    return 'ok';
  }

  // Poll teleop state until the replay completes, fails, or hits the
  // deadline. Every exit path tears down via the (idempotent) finally.
  private async pollReplayUntilDone(
    fail: StepPlayArguments['fail'],
  ): Promise<void> {
    const teleopBotConnector = this.routineContext.teleopBotConnector!;

    const deadline = Date.now() + MAX_REPLAY_SECONDS * 1000;
    let teleopWasActive = false;

    // Re-check teleop state the moment teleop-bot pushes a status transition,
    // or _stop/_pause wakes the waiter; the reconcile interval is the backstop
    // if an event is ever dropped.
    const waiter = createEventDrivenWaiter({
      subscribe: (wake) => {
        teleopBotConnector.subscribeToStateChanged(() => wake());

        return () => teleopBotConnector.unsubscribeFromStateChanged();
      },
      reconcileIntervalMs: RECONCILE_INTERVAL_MS,
    });

    this.replayWaiter = waiter;

    try {
      while (Date.now() < deadline) {
        if (this.stopRequested) {
          // _stop/_pause already issued StopTeleop.
          return await this.failIfPaused(fail);
        }

        await waiter.wait();

        let state;

        try {
          state = await teleopBotConnector.GetTeleopState({});
        } catch (e) {
          log.warn('demonstration.poll', 'Polling teleop state failed', { e });

          // pauseOrFail → Failure skips step.stop() (hasBeenPreempted), so
          // tear down here. Best-effort.
          await this.issueStopTeleop();

          // A pause arriving concurrently with the transport error must win
          // over the less informative "service error".
          const paused = this.failIfPaused(fail);
          if (paused) return paused;

          return fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: 'RunDemonstration',
            },
            failureReason: 'Unable to run demonstration due to service error',
          });
        }

        const status = state?.status;

        if (status === TeleopStatus.TELEOP) {
          teleopWasActive = true;
        }

        if (teleopWasActive && status === TeleopStatus.STOPPED) {
          // STOPPED caused by an in-flight pause is not natural completion.
          const paused = this.failIfPaused(fail);
          if (paused) return paused;

          // Natural completion: bump completedCount so the next play in a
          // loop picks the next position-list entry (mirrors MoveArmTo).
          this.setVariable('completedCount', this.variables.completedCount + 1);

          return undefined;
        }

        if (status === TeleopStatus.ERROR) {
          const errors = state?.errors ?? [];

          return fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: 'RunDemonstration',
            },
            failureReason:
              errors.length > 0
                ? `Demonstration replay failed: ${errors.join('; ')}`
                : 'Demonstration replay failed',
          });
        }
      }

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunDemonstration',
        },
        failureReason: `Demonstration replay did not complete within ${MAX_REPLAY_SECONDS}s`,
      });
    } catch (e) {
      // Surface unexpected throws via fail() — base Step.play expects errors
      // through the fail callback, not an unhandled rejection.
      log.error('demonstration.poll', 'Unexpected error in poll loop', { e });

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunDemonstration',
        },
        failureReason: 'Unable to run demonstration due to service error',
      });
    } finally {
      // Tear down the subscription (idempotent) and drop the reference so a
      // later _stop can't wake a finished loop.
      waiter.stop();
      this.replayWaiter = null;
      await this.issueStopTeleop();
    }
  }

  // Pause is a hard stop — no resume for an in-flight teleop stream. Mirrors
  // TeleopStep.
  public async _pause(args: StepAction): Promise<void> {
    // Mark BEFORE awaiting _stop so the poll loop sees the flag if it ticks
    // during teardown.
    this.isPaused = true;
    this.pauseReason = args.reason;
    await this._stop(args);
  }

  // Best-effort teardown: StopTeleop + release the CSM source. Also called
  // on StartTeleop failure so staged session overrides don't poison the next step.
  private async issueStopTeleop(): Promise<void> {
    // Idempotent per play: only the first caller tears down. A superseded
    // step's orphaned _play loop can run its finally after the next session has
    // started, and StopTeleop is global — issuing it again here would stop that
    // next session's teleop.
    if (this.teleopStopped) {
      return;
    }

    this.teleopStopped = true;

    const { teleopBotConnector, armControlBotConnector } = this.routineContext;

    if (teleopBotConnector) {
      try {
        await teleopBotConnector.StopTeleop({
          clientId: DEMONSTRATION_CLIENT_ID,
        });
      } catch (e) {
        log.warn('demonstration.stop', 'StopTeleop failed', { e });
      }
    }

    // Symmetric with the SetControlSource in _play; safe no-op when the
    // source was never acquired.
    if (getFeatureFlagSync('useControlSourceManager', false)) {
      try {
        await armControlBotConnector?.ReleaseControlFromSource({
          value: getExpectedControlSource(),
          clientId: DEMONSTRATION_CLIENT_ID,
        });
      } catch (e) {
        log.warn('demonstration.stop', 'ReleaseControlFromSource failed', {
          e,
        });
      }
    }
  }

  public async _stop({
    fail: _fail,
  }: Partial<StepPlayArguments> & Pick<StepAction, 'fail'>): Promise<void> {
    this.stopRequested = true;

    // Unblock the replay loop's pending wait immediately so it observes
    // stopRequested now, rather than on the next StateChanged push or the 1s
    // reconcile tick.
    this.replayWaiter?.wakeNow();

    // Cancel an in-flight move-to-start and await the cancellation so _stop
    // doesn't resolve while the arm is still physically moving.
    if (this.activeMotion) {
      const motion = this.activeMotion;

      try {
        motion.emit('cancel');
        await motion.race('failure', 'complete', 'cancelled');
      } catch (e) {
        log.warn('demonstration.stop', 'activeMotion.cancel failed', { e });
      }
    }

    await this.issueStopTeleop();
  }

  private resolvePickedTcpOption(
    positionListID: string,
    positionListIndex: number | undefined,
  ): string | undefined {
    let space;

    try {
      space = this.routineFrame.getSpaceItem(positionListID);
    } catch {
      return undefined;
    }

    if (!('positions' in space)) return undefined;

    const { positions } = space as {
      positions?: Array<{ tcpOption?: string }>;
    };

    if (!positions || positions.length === 0) return undefined;

    const rawIndex = positionListIndex ?? this.variables.completedCount;
    const index = rawIndex % positions.length;

    return positions[index]?.tcpOption;
  }

  // Resolves the move-to-start WRIST pose; also stores the frame transform
  // and the sphere-trim window timestamps forwarded to teleop.
  private async resolveStartPose(): Promise<CartesianPose> {
    const { target, pin, repositionEnabled, sphereRadius } = this.args;

    // _play sets resolvedRecordingId before calling this; never undefined in
    // normal control flow.
    const recordingId = this.resolvedRecordingId;

    if (!recordingId) {
      throw new Error(
        'RunDemonstration: resolveStartPose called before resolvedRecordingId was set',
      );
    }

    // Absent/null ⇒ trim disabled (full replay); <= 0 selects nothing ⇒ fail.
    let radiusMeters: number | null = null;

    if (sphereRadius != null) {
      if (sphereRadius <= 0) {
        throw new Error(
          `RunDemonstration sphere radius must be greater than 0 (got ${sphereRadius} m). ` +
            'Leave it empty to replay the whole recording.',
        );
      }

      radiusMeters = sphereRadius;
    }

    const effectivePin =
      !repositionEnabled && radiusMeters !== null ? 'end' : pin;

    const pickedTcpOption =
      repositionEnabled &&
      target &&
      'positionListID' in target &&
      target.positionListID &&
      target.positionListID !== 'current-position'
        ? this.resolvePickedTcpOption(
            target.positionListID,
            target.positionListIndex,
          )
        : undefined;

    const liveTcpOffsetForScan =
      this.routineContext.getEquipmentOffsetWithoutArmPose('ee-0').tcpPose;

    const {
      frames,
      refWorldPose,
      recordTcpOffset,
      startIndex,
      endIndex,
      hasIdleTrim,
    } = await loadRecordingWindow(
      this.routineContext.feathersApp,
      recordingId,
      radiusMeters,
      effectivePin,
      pickedTcpOption,
      !repositionEnabled,
      liveTcpOffsetForScan,
      this.skillActiveTooltipOption,
    );

    const startFrame = frames[startIndex]!.pose;
    const endFrame = frames[endIndex]!.pose;

    // Forward window timestamps when anything narrows the replay — teleop
    // fetches the raw recording independently and would replay the un-trimmed
    // idle otherwise.
    const narrowsWindow =
      hasIdleTrim || startIndex > 0 || endIndex < frames.length - 1;

    if (narrowsWindow) {
      this.replayStartTimestamp = frames[startIndex]!.timestamp;
      this.replayEndTimestamp = frames[endIndex]!.timestamp;
    } else {
      this.replayStartTimestamp = undefined;
      this.replayEndTimestamp = undefined;
    }

    // Active end-effector composite offset (wrist→TCP), base-aligned.
    const liveTcpOffset =
      this.routineContext.getEquipmentOffsetWithoutArmPose('ee-0').tcpPose;

    const recordTcp = recordTcpOffset ?? liveTcpOffset;

    // Frame re-expression is disabled: recorder and live TCP conventions
    // disagree for the same physical gripper (≈8 cm on an EGK-40), so
    // recordings replay in the frame they were captured in.
    const frameTransform = { ...ZERO_POSE };

    this.demonstrationFrameTransform = frameTransform;

    log.info('demonstration.frameTransform', 'identity (re-expression off)', {
      composedRecordVsLive: additionBetweenPoses(
        recordTcp,
        invertPose(liveTcpOffset),
      ),
    });

    // Reposition OFF: anchor on the frame-transformed window start so the
    // move-to-start matches what teleop streams.
    if (!repositionEnabled) {
      const moveToStart = additionBetweenPoses(startFrame, frameTransform);

      log.info('demonstration.resolveStartPose', 'reposition-off', {
        recordTcp,
        liveTcpOffset,
        frameTransform,
        startFrame,
        moveToStart,
      });

      return moveToStart;
    }

    // Reposition ON: requires a position-list / space target pick.
    if (!target || !('positionListID' in target) || !target.positionListID) {
      throw new Error(
        'RunDemonstration target is not configured (expected a position list)',
      );
    }

    // "Current arm position" sentinel: sample the live WRIST pose — sampling
    // the TCP pose would bake the gripper offset into the anchor.
    if (target.positionListID === 'current-position') {
      const targetPose = await this.routineContext.getCurrentWristPose();
      const sourceTcpOption: TCPOffsetOption = DEFAULT_TCP_OFFSET_OPTION;
      const targetWrist = targetPose;

      const moveToStart = computeMoveToStart({
        pin,
        targetPose,
        targetWrist,
        startFrame,
        endFrame,
        refWorldPose,
        frameTransform,
      });

      log.info(
        'demonstration.resolveStartPose',
        `pin=${pin} (current-position)`,
        {
          sourceTcpOption,
          targetPose,
          targetWrist,
          startFrame,
          endFrame,
          windowStartIndex: startIndex,
          windowEndIndex: endIndex,
          refWorldPose,
          frameTransform,
          moveToStart,
        },
      );

      return moveToStart;
    }

    // getSpaceItem throws if the id is absent, so `space` is always defined.
    const space = this.routineFrame.getSpaceItem(target.positionListID);

    // No explicit index ⇒ cycle through positions by completedCount, wrapping
    // modulo list length (mirrors MoveArmTo / Waypoint).
    const rawIndex = target.positionListIndex ?? this.variables.completedCount;

    const positions =
      'positions' in space
        ? (
            space as {
              positions?: Array<{
                pose?: CartesianPose;
                tcpOption?: TCPOffsetOption;
              }>;
            }
          ).positions
        : undefined;

    const index =
      positions && positions.length > 0
        ? rawIndex % positions.length
        : rawIndex;

    const position = positions?.[index];

    let targetPose: CartesianPose;
    // How the target was taught; the reference pin ignores it (raw wrist).
    let sourceTcpOption: TCPOffsetOption = DEFAULT_TCP_OFFSET_OPTION;

    if (position?.pose) {
      targetPose = position.pose;
      sourceTcpOption = position.tcpOption ?? DEFAULT_TCP_OFFSET_OPTION;

      // Apply local-accuracy calibration when the list is anchored to a
      // calibration grid (mirrors Waypoint / MoveArmTo).
      const anchorId =
        'anchoredToID' in space
          ? (space as { anchoredToID?: string }).anchoredToID
          : undefined;

      if (anchorId) {
        let anchor;

        try {
          anchor = this.routineFrame.getSpaceItem(anchorId);
        } catch {
          anchor = undefined;
        }

        if (anchor && anchor.kind === 'localAccuracyCalibration') {
          targetPose = this.routineContext.getCalibratedGoal(
            targetPose,
            anchor,
          );
        }
      }
    } else if ('pose' in space) {
      // environmentObject: a world-frame object pose.
      targetPose = (space as { pose: CartesianPose }).pose;
    } else {
      throw new Error(
        `Position list ${target.positionListID} has no entry at index ${index}`,
      );
    }

    // start/end read the target reduced to wrist by its taught tcpOption;
    // reference uses the raw target.
    const targetWrist = this.routineContext.reducePoseToWristFrame(
      targetPose,
      sourceTcpOption,
    );

    const moveToStart = computeMoveToStart({
      pin,
      targetPose,
      targetWrist,
      startFrame,
      endFrame,
      refWorldPose,
      frameTransform,
    });

    log.info('demonstration.resolveStartPose', `pin=${pin}`, {
      sourceTcpOption,
      targetPose,
      targetWrist,
      startFrame,
      endFrame,
      windowStartIndex: startIndex,
      windowEndIndex: endIndex,
      refWorldPose,
      frameTransform,
      moveToStart,
    });

    return moveToStart;
  }

  // Single-pose move via the standard planner (mirrors MoveArmToV2).
  // Tri-state: 'failed' ⇒ fail() was already invoked by the failure listener
  // — callers must NOT fail() again or they'd overwrite the real reason;
  // 'cancelled' ⇒ intentional stop/pause — callers route via failIfPaused.
  private async moveArmTo(
    pose: CartesianPose,
    fail: StepPlayArguments['fail'],
  ): Promise<'completed' | 'failed' | 'cancelled'> {
    // `pose` is a WRIST pose. Pin the move to the wrist so the planner places
    // the flange — not whatever TCP a prior step left active — at `pose`.
    const target: ArmTarget = {
      motionKind: 'joint',
      pose,
      effectiveTCPOption: 'wrist',
      useDeterministicPlanner: getFeatureFlagSync(
        'useDeterministicPlanner',
        false,
      ),
    };

    let completed = false;
    let failed = false;

    const motion = this.routineContext.doMotion({
      request: { targets: [target] as [ArmTarget] },
      speedProfiles: [DEFAULT_SPEED_PROFILE],
      stepID: this.id,
    });

    this.activeMotion = motion;

    motion.on('complete', () => {
      completed = true;
    });

    motion.on('failure', (failure) => {
      failed = true;

      // Wrap the planner failure so the operator knows the move-to-start
      // failed (vs. the replay itself).
      fail({
        ...failure,
        failureReason: `Unable to reach demonstration start position. ${
          failure.failureReason ?? ''
        }`.trim(),
      });
    });

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      motion.removeAllListeners();
      this.activeMotion = null;
    }

    if (failed) return 'failed';
    if (completed) return 'completed';

    return 'cancelled';
  }
}
